import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  TextField,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { globalVariables } from "../../global/GlobalVariables";
import { getLanguageCookie } from "../../language/LanguageFunctions";
import { postNotification } from "../../components/utilities/Notifications";
import {
  delay,
  findGetParameter,
  getData
} from "../../components/utilities/UtilityFunctions";
import {
  getEnvironmentVariable,
  supportsUYPlatformFeatures
} from "../../global/environmentVariables";
import { refreshTokens } from "../../components/user/BCPAuth";
import { MyWarhammerLinkButton } from "../../components/alerts/DefaultAlerts";
import { getCookie } from "../../components/layout/cookies/CookieFunctions";
import SubscriptionSessionCheckout from "./stripe/SubscriptionSessionCheckout";
import SubscriptionIntentCheckout from "./stripe/SubscriptionIntentCheckout";

// TODO: ZAR vs USD is really a market setting, not a UY platform feature
const SUPPORTS_UY_PLATFORM_FEATURES = supportsUYPlatformFeatures();
const USE_LEGACY_SUBSCRIPTION_FORM =
  getEnvironmentVariable("USE_LEGACY_SUBSCRIPTION_FORM") === "true";

export default function SubscriptionCheckout() {
  const authContext = useContext(AuthContext);
  const globalVars = globalVariables();
  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "pages.subscriptionCheckout"
  });
  const productId = findGetParameter("productId");
  const priceId = findGetParameter("priceId");
  const gameStoreId =
    findGetParameter("gameStoreId") || findGetParameter("orgId");
  const affiliate =
    findGetParameter("affiliate") || getCookie("affiliate") || null;

  const [couponCode, setCouponCode] = useState(
    findGetParameter("coupon") || ""
  );
  const [coupon, setCoupon] = useState(null);
  const [couponStatus, setCouponStatus] = useState(null);

  const [confirmOk, setConfirmOk] = useState(false);

  const [price, setPrice] = useState(null);

  const [gameStore, setGameStore] = useState(null);

  const handleCouponCodeChanges = (e) => {
    let newCouponCode = (e.target?.value || "").toUpperCase();
    let newCoupon = undefined;
    setCouponCode(newCouponCode);
    if (
      newCouponCode !== "" &&
      globalVars?.pricing?.products?.[productId]?.coupons?.[newCouponCode]
    ) {
      newCoupon = {
        couponCode: newCouponCode,
        ...globalVars.pricing.products[productId].coupons[newCouponCode]
      };
      if (
        newCoupon?.frequencies &&
        price?.frequency &&
        !newCoupon?.frequencies[price.frequency]
      ) {
        setCoupon(null);
      } else {
        setCoupon(newCoupon);
      }
    } else {
      setCoupon(null);
    }

    if (newCoupon) {
      setCouponStatus({ success: true });
    } else if (e.type === "blur" && !newCoupon) {
      setCouponStatus({ error: t("invalidCouponCode") });
    } else {
      setCouponStatus(null);
    }
  };

  const pollIdentity = async (identityToken) => {
    if (
      couponCode === "TACOMA25" &&
      !authContext?.authState?.identity?.hasMyWarhammer
    ) {
      await delay(5000);
      await refreshTokens();
    }
  };

  useEffect(() => {
    var prices = globalVars?.pricing?.products?.[productId]?.prices;

    if (
      couponCode &&
      globalVars?.pricing?.products?.[productId]?.coupons?.[couponCode]
    ) {
      setCoupon({
        couponCode: couponCode,
        ...globalVars.pricing.products[productId].coupons[couponCode]
      });

      if (
        couponCode !== "TACOMA25" ||
        authContext?.authState?.identity?.hasMyWarhammer
      ) {
        setConfirmOk(true);
      } else {
        pollIdentity();
      }
    }

    if (prices?.length) {
      prices = prices.filter(function (el) {
        return el.priceId === priceId;
      });

      if (prices?.length) {
        setPrice(prices[0]);
      }
    }

    if (gameStoreId && gameStoreId !== "") {
      const fetchGameStore = async () => {
        try {
          const apiStore = await getData(`/gamestores/${gameStoreId}`);
          if (apiStore) setGameStore(apiStore);
        } catch (err) {
          postNotification("error", err);
        }
      };

      fetchGameStore();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid container sx={{ gap: 2 }}>
          <Grid item xs={12} sx={{ p: 2 }}>
            <Typography variant="h3" gutterBottom>
              {globalVars?.pricing?.products?.[productId]?.name ||
                t("subscription", {
                  keyPrefix: "components.Footer",
                  defaultValue: "Subscription"
                })}{" "}
              {t("checkout")}
              {gameStore?.name ? ` - ${gameStore.name}` : ""}
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ p: 2 }}>
            {priceId && (
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Typography variant="body1" gutterBottom>
                    {t("product")}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography
                    variant="body1"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {globalVars?.pricing?.products?.[productId]?.name ||
                      "Subscription"}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body1" gutterBottom>
                    {t("currency")}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography
                    variant="body1"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {SUPPORTS_UY_PLATFORM_FEATURES ? "ZAR" : "USD"}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="body1" gutterBottom>
                    {t("price")}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  {price && (
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {!SUPPORTS_UY_PLATFORM_FEATURES ? "$" : ""}
                      {price.amount.toFixed(2)}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="body1" gutterBottom>
                    {t("couponCode")}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    name="couponCode"
                    label={t("couponCode")}
                    value={couponCode}
                    variant="outlined"
                    size="small"
                    margin="none"
                    onBlur={handleCouponCodeChanges}
                    onChange={handleCouponCodeChanges}
                    error={Boolean(couponStatus?.error)}
                    helperText={couponStatus?.error}
                    color={couponStatus?.success ? "success" : "primary"}
                    disabled={confirmOk}
                  />
                </Grid>

                {coupon?.discount && (
                  <>
                    <Grid item xs={4}>
                      <Typography variant="body1" gutterBottom>
                        {coupon?.discount?.onceOff
                          ? `${
                              price.frequency === "Yearly"
                                ? t("firstYear")
                                : price.frequency === "Monthly"
                                  ? t("firstMonth")
                                  : t("firstBillingPeriod")
                            } `
                          : ""}
                        {t("discount")}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      {price && (
                        <Typography
                          variant="body1"
                          gutterBottom
                          sx={{ fontWeight: 600 }}
                        >
                          $
                          {(coupon?.discount?.percentage
                            ? price.amount * coupon.discount.percentage
                            : 0
                          ).toFixed(2)}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body1" gutterBottom>
                        {coupon?.discount?.onceOff
                          ? `${
                              price.frequency === "Yearly"
                                ? t("firstYear")
                                : price.frequency === "Monthly"
                                  ? t("firstMonth")
                                  : t("firstBillingPeriod")
                            } `
                          : ""}
                        {t("total")}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      {price && (
                        <Typography
                          variant="body1"
                          gutterBottom
                          sx={{ fontWeight: 600 }}
                        >
                          $
                          {(coupon?.discount?.percentage
                            ? price.amount * (1 - coupon.discount.percentage)
                            : price.amount
                          ).toFixed(2)}
                        </Typography>
                      )}
                    </Grid>
                  </>
                )}
                <Grid item xs={4}>
                  <Typography variant="body1" gutterBottom>
                    {t("billingFrequency")}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  {price && (
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {price.frequency === "Monthly"
                        ? t("monthlyCapitalized")
                        : t("yearlyCapitalized")}
                    </Typography>
                  )}
                </Grid>

                {!confirmOk && !gameStoreId && (
                  <>
                    {couponCode === "TACOMA25" &&
                    coupon &&
                    !authContext?.authState?.identity?.hasMyWarhammer ? (
                      <>
                        <Grid item xs={12}>
                          <MyWarhammerLinkButton fullWidth target="_blank" />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography>
                            This discount code requires you link your My
                            Warhammer account to your BCP account.
                          </Typography>
                        </Grid>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </Grid>
            )}
          </Grid>
          {(gameStoreId && gameStore?.subscribed) ||
          (!gameStoreId && authContext?.authState?.identity?.subscriber) ? (
            <Grid item xs={12} sx={{ p: 2 }}>
              <Typography variant="body1" gutterBottom>
                {t("youAlreadyHaveA")}{" "}
                {globalVars?.pricing?.products?.[productId]?.name ||
                  "Subscription"}
                . {t("ifYouWishToUpdate")}{" "}
                <Link
                  href={
                    gameStoreId
                      ? `/organize/organization/${gameStoreId}?active_tab=subscriptions`
                      : "/account?active_tab=subscriptions"
                  }
                  underline="none"
                >
                  {gameStoreId ? t("gameStore") : t("account")}
                </Link>
              </Typography>
            </Grid>
          ) : gameStoreId && gameStoreId !== "" ? (
            <>
              {USE_LEGACY_SUBSCRIPTION_FORM ? (
                <>
                  {!confirmOk ? (
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        onClick={() => setConfirmOk(true)}
                      >
                        {t("confirm", {
                          keyPrefix: "buttons",
                          defaultValue: "Confirm"
                        })}
                      </Button>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      xs={12}
                      sx={{
                        p: 2,
                        minHeight: ["420px", "420px", "360px"]
                      }}
                    >
                      <SubscriptionIntentCheckout
                        authContext={authContext}
                        priceId={priceId}
                        product={
                          globalVars?.pricing?.products?.[productId] || null
                        }
                        couponCode={couponCode}
                        coupon={coupon}
                        gameStoreId={gameStoreId}
                      />
                    </Grid>
                  )}
                </>
              ) : (
                <Grid item xs={12}>
                  <SubscriptionSessionCheckout
                    authContext={authContext}
                    priceId={priceId}
                    product={globalVars?.pricing?.products?.[productId] || null}
                    couponCode={couponCode}
                    coupon={coupon}
                    affiliate={affiliate}
                    setConfirmOk={setConfirmOk}
                    gameStoreId={gameStoreId}
                  />
                </Grid>
              )}
            </>
          ) : (
            <>
              {USE_LEGACY_SUBSCRIPTION_FORM ? (
                <>
                  {!confirmOk ? (
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        onClick={() => setConfirmOk(true)}
                      >
                        {t("confirm", {
                          keyPrefix: "buttons",
                          defaultValue: "Confirm"
                        })}
                      </Button>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      xs={12}
                      sx={{
                        p: 2,
                        minHeight: ["420px", "420px", "360px"]
                      }}
                    >
                      <SubscriptionIntentCheckout
                        authContext={authContext}
                        priceId={priceId}
                        product={
                          globalVars?.pricing?.products?.[productId] || null
                        }
                        couponCode={couponCode}
                        coupon={coupon}
                      />
                    </Grid>
                  )}
                </>
              ) : (
                <Grid item xs={12}>
                  <SubscriptionSessionCheckout
                    authContext={authContext}
                    priceId={priceId}
                    product={globalVars?.pricing?.products?.[productId] || null}
                    couponCode={couponCode}
                    coupon={coupon}
                    affiliate={affiliate}
                    setConfirmOk={setConfirmOk}
                    userId={authContext?.authState?.identity?.userId}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Container>
    </Box>
  );
}
